import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import { Row, Col, Card, Form, Button, Spinner } from "react-bootstrap";
import PageLayout from "../../../layouts/PageLayout";
import {
    FaSearch, FaPrint, FaArrowUp, FaArrowDown,
    FaChartLine, FaFilter, FaTimes, FaShoppingCart,
    FaStethoscope, FaMoneyBill, FaWallet, FaCreditCard,
    FaBoxes, FaFileInvoice, FaCalendarAlt
} from "react-icons/fa";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import FinanceCss from "../../../assets/fonts/finance_report.css";

export default function FinanceReport() {
    const { t } = useContext(TranslatorContext);
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState([]);
    const [summary, setSummary] = useState({
        total_sales: 0,
        total_appointments: 0,
        total_income: 0,
        total_purchases: 0,
        total_expenses: 0,
        total_expense: 0,
        net_profit: 0
    });
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [showFilters, setShowFilters] = useState(true);
    const [reportPeriod, setReportPeriod] = useState({ start_date: '', end_date: '' });
    const [selectedMonth, setSelectedMonth] = useState('');

    const formatDateForAPI = (date) => {
        if (!date) return null;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    useEffect(() => {
        const today = new Date();
        setStartDate(today);
        setEndDate(today);
        fetchReport();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        try {
            const params = {};
            if (startDate) params.start_date = formatDateForAPI(startDate);
            if (endDate) params.end_date = formatDateForAPI(endDate);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/finance-report`,
                {
                    params: params,
                    headers: { Authorization: `Bearer ${localStorage.getItem("authToken")}` }
                }
            );

            if (response.data && response.data.status) {
                setReportData(response.data.details || []);
                setSummary(response.data.summary || {
                    total_sales: 0,
                    total_appointments: 0,
                    total_income: 0,
                    total_purchases: 0,
                    total_expenses: 0,
                    total_expense: 0,
                    net_profit: 0
                });
                setReportPeriod({
                    start_date: params.start_date || formatDateForAPI(new Date()),
                    end_date: params.end_date || formatDateForAPI(new Date())
                });
            }
        } catch (error) {
            console.error("Error fetching report:", error);
            alert(t('failed_to_load_report'));
        } finally {
            setLoading(false);
        }
    };

    const handleMonthChange = (e) => {
        const monthValue = e.target.value;
        setSelectedMonth(monthValue);

        if (monthValue) {
            const [year, month] = monthValue.split('-');
            // Create dates in local timezone, not UTC
            const startDateObj = new Date(parseInt(year), parseInt(month) - 1, 1);
            const endDateObj = new Date(parseInt(year), parseInt(month), 0);

            // Set end date to end of day (23:59:59) for inclusive range
            endDateObj.setHours(23, 59, 59, 999);

            setStartDate(startDateObj);
            setEndDate(endDateObj);
        } else {
            const today = new Date();
            setStartDate(today);
            setEndDate(today);
        }
    };

    const handleFilter = () => {
        fetchReport();
    };

    const handleReset = () => {
        const today = new Date();
        setStartDate(today);
        setEndDate(today);
        setSelectedMonth('');
        setTimeout(() => fetchReport(), 100);
    };

    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0.00';
        return parseFloat(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const exportToPDF = () => {
        let url = `${process.env.REACT_APP_URL}/finance-report/pdf`;
        const params = new URLSearchParams();
        if (startDate) params.append("start_date", formatDateForAPI(startDate));
        if (endDate) params.append("end_date", formatDateForAPI(endDate));
        const finalUrl = `${url}?${params.toString()}`;
        window.open(finalUrl, "_blank");
    };

    const handleStartDateChange = (date) => {
        if (date) {
            setStartDate(date);
            setSelectedMonth('');
        } else {
            setStartDate(null);
        }
    };

    const handleEndDateChange = (date) => {
        if (date) {
            setEndDate(date);
            setSelectedMonth('');
        } else {
            setEndDate(null);
        }
    };

    // Calculate totals from summary
    const totalIncome = summary.total_income || (summary.total_sales + summary.total_appointments);
    const totalExpense = summary.total_expense || (summary.total_purchases + summary.total_expenses);
    const netProfit = summary.net_profit || (totalIncome - totalExpense);

    // Filter data by type
    const incomeData = reportData.filter(item => item.type === 'Sale' || item.type === 'Appointment');
    const expenseData = reportData.filter(item => item.type === 'Purchase' || item.type === 'Expense');

    // Generate month options (last 12 months)
    const getMonthOptions = () => {
        const options = [];
        const currentDate = new Date();
        for (let i = 0; i < 12; i++) {
            const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const monthName = date.toLocaleString('default', { month: 'long' });
            options.push({
                value: `${year}-${month}`,
                label: `${monthName} ${year}`
            });
        }
        return options;
    };

    // Format details for display
    const formatDetails = (details) => {
        if (!details || details.length === 0) return '-';
        return details.map((d, i) => {
            if (d.product_name && d.quantity) {
                return `${d.product_name} x${d.quantity}`;
            }
            else if (d.name && d.qty) {
                return `${d.name} x${d.qty}`;
            }
            else if (d.name && d.amount) {
                return `${d.name}: ${formatCurrency(d.amount)}`;
            }
            else if (d.name) {
                return d.name;
            }
            return '';
        }).join(', ');
    };

    // Income Table Component
    const IncomeTable = () => (
        <div className="mc-table-responsive mb-4">
            <h5 className="mb-3">
                <FaArrowUp className="text-success me-2" />
                Income Transactions (Sales + Appointments)
            </h5>
            <table className="mc-table income-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>ID</th>
                        <th>Customer</th>
                        <th>Date</th>
                        <th>Details</th>
                        <th>Paid Amount</th>
                        <th>Payment Methods</th>
                    </tr>
                </thead>
                <tbody>
                    {incomeData.length === 0 ? (
                        <tr className="empty-row">
                            <td colSpan="8">No income transactions found</td>
                        </tr>
                    ) : (
                        incomeData.map((item, idx) => (
                            <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>
                                    <span className={`badge ${item.type === 'Sale' ? 'bg-success' : 'bg-info'}`}>
                                        {item.type}
                                    </span>
                                </td>
                                <td>{item.id}</td>
                                <td>{item.customer || item.vendor || '-'}</td>
                                <td>{formatDate(item.date)}</td>
                                <td className="details-cell">
                                    <div className="details-scroll">
                                        <small className="badge bg-light text-dark">
                                            {formatDetails(item.details)}
                                        </small>
                                    </div>
                                </td>
                                <td className="amount-cell">
                                    <strong>{formatCurrency(item.paid_amount)}</strong>
                                </td>
                                <td className="payment-cell">
                                    {item.payments && item.payments.map((p, i) => (
                                        <div key={i}>
                                            <small>{p.method}: {formatCurrency(p.amount)}</small>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
                <tfoot>
                    <tr className="total-row">
                        <td colSpan="6">Total Income:</td>
                        <td className="amount-cell">{formatCurrency(incomeData.reduce((sum, i) => sum + (i.paid_amount || 0), 0))}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    // Expense Table Component
    const ExpenseTable = () => (
        <div className="mc-table-responsive mb-4">
            <h5 className="mb-3">
                <FaArrowDown className="text-danger me-2" />
                Expense Transactions (Purchases + Expenses)
            </h5>
            <table className="mc-table expense-table">
                <thead>
                    <tr>
                        <th>#</th>
                        <th>Type</th>
                        <th>ID</th>
                        <th>Vendor/Category</th>
                        <th>Date</th>
                        <th>Details</th>
                        <th>Paid Amount</th>
                        <th>Payment Methods</th>
                    </tr>
                </thead>
                <tbody>
                    {expenseData.length === 0 ? (
                        <tr className="empty-row">
                            <td colSpan="8">No expense transactions found</td>
                        </tr>
                    ) : (
                        expenseData.map((item, idx) => (
                            <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td>
                                    <span className={`badge ${item.type === 'Purchase' ? 'bg-warning' : 'bg-danger'}`}>
                                        {item.type}
                                    </span>
                                </td>
                                <td>{item.id}</td>
                                <td>{item.vendor || item.customer || item.title || '-'}</td>
                                <td>{formatDate(item.date)}</td>
                                <td className="details-cell">
                                    <div className="details-scroll">
                                        <small className="badge bg-light text-dark">
                                            {formatDetails(item.details)}
                                        </small>
                                    </div>
                                </td>
                                <td className="amount-cell">
                                    <strong>{formatCurrency(item.paid_amount)}</strong>
                                </td>
                                <td className="payment-cell">
                                    {item.payments && item.payments.map((p, i) => (
                                        <div key={i}>
                                            <small>{p.method}: {formatCurrency(p.amount)}</small>
                                        </div>
                                    ))}
                                </td>
                            </tr>
                        ))
                    )}
                </tbody>
                <tfoot>
                    <tr className="total-row">
                        <td colSpan="6">Total Expense:</td>
                        <td className="amount-cell">{formatCurrency(expenseData.reduce((sum, e) => sum + (e.paid_amount || 0), 0))}</td>
                        <td></td>
                    </tr>
                </tfoot>
            </table>
        </div>
    );

    return (
        <PageLayout>
            <div className="finance-report-container">
                <div className="mb-4">
                    <h2 className="mb-1">
                        <FaChartLine className="me-2" />
                        {t('Finance Report')}
                    </h2>
                    <p className="text-muted mb-0">{t('View income, expenses, and financial summary')}</p>
                    {reportPeriod.start_date && reportPeriod.end_date && (
                        <p className="text-muted small mt-1">
                            {t('Report Period')}: {formatDate(reportPeriod.start_date)} - {formatDate(reportPeriod.end_date)}
                        </p>
                    )}
                </div>

                {/* Filter Section */}
                <div className="card mb-4 filter-card">
                    <div className="card-body">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h5 className="mb-0">
                                <FaFilter className="me-2" />
                                {t('Filters')}
                            </h5>
                            <Button
                                variant="link"
                                onClick={() => setShowFilters(!showFilters)}
                                className="text-decoration-none"
                            >
                                {showFilters ? <FaTimes /> : <FaFilter />}
                            </Button>
                        </div>

                        {showFilters && (
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <Form.Label className="fw-semibold">
                                        <FaCalendarAlt className="me-1" />
                                        {t('Select Month')}
                                    </Form.Label>
                                    <Form.Select
                                        value={selectedMonth}
                                        onChange={handleMonthChange}
                                    >
                                        <option value="">Select a month</option>
                                        {getMonthOptions().map(option => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </div>

                                <div className="col-md-2">
                                    <Form.Label className="fw-semibold">{t('Start Date')}</Form.Label>
                                    <DatePicker
                                        selected={startDate}
                                        onChange={handleStartDateChange}
                                        className="form-control"
                                        placeholderText="Select start date"
                                        dateFormat="yyyy-MM-dd"
                                        isClearable
                                        maxDate={endDate || new Date()}
                                    />
                                </div>

                                <div className="col-md-2">
                                    <Form.Label className="fw-semibold">{t('End Date')}</Form.Label>
                                    <DatePicker
                                        selected={endDate}
                                        onChange={handleEndDateChange}
                                        className="form-control"
                                        placeholderText="Select end date"
                                        dateFormat="yyyy-MM-dd"
                                        isClearable
                                        minDate={startDate}
                                        maxDate={new Date()}
                                    />
                                </div>

                                <div className="col-md-3 d-flex align-items-end">
                                    <Button variant="primary" onClick={handleFilter} className="me-2">
                                        <FaSearch className="me-2" />
                                        {t('Search')}
                                    </Button>
                                    <Button variant="secondary" onClick={handleReset}>
                                        {t('Reset')}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Summary Cards */}
                <div className="row g-3 mb-4">
                    {/* Total Income Card */}
                    <div className="col-md-4">
                        <div className="card border-success h-100 summary-card income-card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Income</h6>
                                        <h2 className="mb-0 text-success">{formatCurrency(totalIncome)}</h2>
                                    </div>
                                    <div className="text-success">
                                        <FaArrowDown className="text-success " size={32} />
                                    </div>
                                </div>
                                {/* <div className="row mt-3">
                                    <div className="col-6">
                                        <small className="text-muted">Sales Revenue</small>
                                        <p className="mb-0 fw-bold">{formatCurrency(summary.total_sales)}</p>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Appointments Revenue</small>
                                        <p className="mb-0 fw-bold">{formatCurrency(summary.total_appointments)}</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>

                    {/* Total Expense Card */}
                    <div className="col-md-4">
                        <div className="card border-danger h-100 summary-card expense-card">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Total Expense</h6>
                                        <h2 className="mb-0 text-danger">{formatCurrency(totalExpense)}</h2>
                                    </div>
                                    <div className="text-danger">
                                        <FaArrowUp className="text-danger" size={32} />
                                    </div>
                                </div>
                                {/* <div className="row mt-3">
                                    <div className="col-6">
                                        <small className="text-muted">Purchases</small>
                                        <p className="mb-0 fw-bold">{formatCurrency(summary.total_purchases)}</p>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Other Expenses</small>
                                        <p className="mb-0 fw-bold">{formatCurrency(summary.total_expenses)}</p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className={`card net-profit-card ${netProfit >= 0 ? 'profit-card' : 'loss-card'}`}>
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <h6 className="text-muted mb-1">Net Profit / Loss</h6>
                                        <h1 className={`mb-0 ${netProfit >= 0 ? 'text-success' : 'text-danger'}`}>
                                            {netProfit >= 0 ? '+' : '-'}{formatCurrency(Math.abs(netProfit))}
                                        </h1>
                                    </div>
                                    <div >
                                        {netProfit >= 0 ? (
                                            <FaArrowDown className="text-success" size={32} />
                                        ) : (
                                            <FaArrowUp className="text-danger" size={32} />
                                        )}
                                    </div>
                                </div>
                                {/* <div className="row mt-3 pt-2 border-top">
                                    <div className="col-6">
                                        <small className="text-muted">Total Transactions</small>
                                        <p className="mb-0 fw-bold">{reportData.length}</p>
                                    </div>
                                    <div className="col-6">
                                        <small className="text-muted">Period</small>
                                        <p className="mb-0 fw-bold small">
                                            {formatDate(reportPeriod.start_date)} - {formatDate(reportPeriod.end_date)}
                                        </p>
                                    </div>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="mb-3 d-flex justify-content-end no-print">
                    <Button variant="danger" onClick={exportToPDF}>
                        <FaPrint className="me-2" />
                        {t('Export to PDF')}
                    </Button>
                </div>

                {/* Main Transactions Tables */}
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" />
                        <p className="mt-2">{t('Loading...')}</p>
                    </div>
                ) : (
                    <>
                        <IncomeTable />
                        <ExpenseTable />
                    </>
                )}
            </div>

        </PageLayout>
    );
}